/**
 * The Justice Delivery Index, in one mutable object — the same arrangement as
 * `home.ts`: the CMS fills it in at startup, and these values stand until then.
 *
 * The bundled year is 2022, from the legacy map dataset (`map_data_sample.js`),
 * with rank, health and a plain description derived the way the API derives
 * them. `null` means the state declined to publish — reported as a finding,
 * never estimated around.
 */

export type Health = 'Good' | 'Fair' | 'Poor';

export interface JdiRow {
    state: string;
    /** Average days from filing to judgment, all cases. */
    avgDays: number | null;
    civilDays: number | null;
    criminalDays: number | null;
    /** 1 = the fewest days among the states that published. */
    rank: number | null;
    health: Health | null;
    /** What the figures say, in a sentence or three. */
    description: string;
}

export interface JdiContent {
    years: string[];
    /** The year shown first — the latest. */
    year: string;
    byYear: Record<string, JdiRow[]>;
}

/** Display label for a state ("Ogun State", but "FCT Abuja" stays as-is). */
export const stateLabel = (state: string): string => (state.startsWith('FCT') ? state : `${state} State`);

const ordinal = (n: number): string => {
    const mod100 = n % 100;
    if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
    return `${n}${{ 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th'}`;
};
const fmt = (n: number): string => Math.round(n).toLocaleString('en-NG');

/** Rank, health and description for the bundled rows — the API does the same for what it serves. */
function derive(year: string, raw: Omit<JdiRow, 'rank' | 'health' | 'description'>[]): JdiRow[] {
    const reporting = raw.filter((r) => r.avgDays !== null).sort((a, b) => a.avgDays! - b.avgDays!);
    const position = new Map(reporting.map((r, i) => [r.state, i]));
    const n = reporting.length;
    const average = n ? reporting.reduce((sum, r) => sum + r.avgDays!, 0) / n : 0;
    return raw.map((r) => {
        const i = position.get(r.state);
        const label = stateLabel(r.state);
        if (i === undefined || r.avgDays === null) {
            return {
                ...r,
                rank: null,
                health: null,
                description: `${label} did not publish its ${year} case-duration figures. A withheld return is reported as a finding in its own right; nothing here is estimated.`,
            };
        }
        const health: Health = i < n / 3 ? 'Good' : i < (2 * n) / 3 ? 'Fair' : 'Poor';
        let description = `In ${year}, ${label}'s High Court took an average of ${fmt(r.avgDays)} days to conclude a case`;
        if (r.civilDays !== null && r.criminalDays !== null) {
            description += ` — ${fmt(r.civilDays)} days for civil matters and ${fmt(r.criminalDays)} for criminal`;
        }
        description += `, ranking ${ordinal(i + 1)} of the ${n} states that published their figures.`;
        const diff = ((average - r.avgDays) / average) * 100;
        if (Math.abs(diff) < 3) description += ` That is close to the national average of ${fmt(average)} days.`;
        else if (diff > 0)
            description += ` That is ${Math.round(diff)}% faster than the national average of ${fmt(average)} days.`;
        else description += ` That is ${Math.round(-diff)}% slower than the national average of ${fmt(average)} days.`;
        return { ...r, rank: i + 1, health, description };
    });
}

const BUNDLED_2022 = derive('2022', [
    { state: 'Abia', avgDays: 730, civilDays: 722, criminalDays: 788 },
    { state: 'Adamawa', avgDays: 424, civilDays: 229, criminalDays: 1236 },
    { state: 'Akwa Ibom', avgDays: 1023, civilDays: 1080, criminalDays: 792 },
    { state: 'Anambra', avgDays: 1206, civilDays: 1357, criminalDays: 774 },
    { state: 'Bauchi', avgDays: 561, civilDays: 676, criminalDays: 332 },
    { state: 'Bayelsa', avgDays: 721, civilDays: 875, criminalDays: 395 },
    { state: 'Benue', avgDays: 712, civilDays: 715, criminalDays: 691 },
    { state: 'Borno', avgDays: 205, civilDays: 230, criminalDays: 158 },
    { state: 'Cross River', avgDays: 681, civilDays: 572, criminalDays: 1178 },
    { state: 'Delta', avgDays: 897, civilDays: 818, criminalDays: 1083 },
    { state: 'Ebonyi', avgDays: 480, civilDays: 410, criminalDays: 767 },
    { state: 'Edo', avgDays: 800, civilDays: 823, criminalDays: 1341 },
    { state: 'Ekiti', avgDays: null, civilDays: null, criminalDays: null },
    { state: 'Enugu', avgDays: 861, civilDays: 808, criminalDays: 1155 },
    { state: 'FCT Abuja', avgDays: 1431, civilDays: 1477, criminalDays: 1188 },
    { state: 'Gombe', avgDays: 366, civilDays: 465, criminalDays: 256 },
    { state: 'Imo', avgDays: 868, civilDays: 830, criminalDays: 989 },
    { state: 'Jigawa', avgDays: 472, civilDays: 317, criminalDays: 553 },
    { state: 'Kaduna', avgDays: 694, civilDays: 694, criminalDays: 700 },
    { state: 'Kano', avgDays: 771, civilDays: 714, criminalDays: 850 },
    { state: 'Katsina', avgDays: 229, civilDays: 167, criminalDays: 475 },
    { state: 'Kebbi', avgDays: 365, civilDays: 332, criminalDays: 405 },
    { state: 'Kogi', avgDays: 381, civilDays: 434, criminalDays: 263 },
    { state: 'Kwara', avgDays: 612, civilDays: 587, criminalDays: 770 },
    { state: 'Lagos', avgDays: 917, civilDays: 979, criminalDays: 759 },
    { state: 'Nasarawa', avgDays: 1044, civilDays: 1181, criminalDays: 466 },
    { state: 'Niger', avgDays: 512, civilDays: 592, criminalDays: 182 },
    { state: 'Ogun', avgDays: 509, civilDays: 503, criminalDays: 501 },
    { state: 'Ondo', avgDays: 414, civilDays: 331, criminalDays: 652 },
    { state: 'Osun', avgDays: 801, civilDays: 836, criminalDays: 580 },
    { state: 'Oyo', avgDays: 832, civilDays: 806, criminalDays: 950 },
    { state: 'Plateau', avgDays: 814, civilDays: 821, criminalDays: 755 },
    { state: 'Rivers', avgDays: 1731, civilDays: 1771, criminalDays: 1571 },
    { state: 'Sokoto', avgDays: null, civilDays: null, criminalDays: null },
    { state: 'Taraba', avgDays: 517, civilDays: 490, criminalDays: 562 },
    { state: 'Yobe', avgDays: null, civilDays: null, criminalDays: null },
    { state: 'Zamfara', avgDays: null, civilDays: null, criminalDays: null },
]);

export const JDI: JdiContent = {
    years: ['2022'],
    year: '2022',
    byYear: { '2022': BUNDLED_2022 },
};

/** The rows for a year, if the index has that year. */
export const jdiRows = (year: string): JdiRow[] => JDI.byYear[year] ?? [];

export const jdiRow = (year: string, state: string): JdiRow | undefined => jdiRows(year).find((r) => r.state === state);
